import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ArrowRight,
  BadgeCheck,
  Eye,
  Filter,
  Hourglass,
  Info,
  Loader2,
  Search,
} from "lucide-react";

import PlanStateLabel from "./PlanStateLabel";
import { BriefingQuestionsStrip } from "./PortfolioModal";
import { hasBriefing } from "./PortfolioQuickLook";
import { daysSince, shortDate } from "./shared";
import type { PortfolioAccountRow } from "../../Store/Reducers/MyPortfolioOverviewSlice";
import type { AccountPlanStatus } from "../../Types/AccountPlan";

/**
 * The sponsor's Account Plans list: where to focus, by attention signals. The
 * account team's list is TeamPlansList; this one kept its layout in the 2026-10-29
 * redesign (the mock covered the AE/CSM list only).
 */

type StatusFilter = "all" | "ingested" | "awaiting" | "processing" | "sponsor";

const FILTERS: { id: StatusFilter; label: string }[] = [
  { id: "all", label: "All accounts" },
  { id: "ingested", label: "Plan ingested" },
  { id: "awaiting", label: "Awaiting upload" },
  { id: "processing", label: "Processing" },
  { id: "sponsor", label: "Briefing available" },
];

const matchesFilter = (f: StatusFilter, s?: AccountPlanStatus) => {
  switch (f) {
    case "ingested":
      return !!s?.viewer_ready;
    case "awaiting":
      return !s?.latest_file;
    case "processing":
      return s?.latest_file?.status === "processing";
    case "sponsor":
      return hasBriefing(s);
    default:
      return true;
  }
};

const COLUMNS = ["Account", "Attention signals", "ARR", "Renewal", "Briefing"];

const COLUMN_WIDTH: Record<string, string> = {
  "Attention signals": "w-40",
  ARR: "w-24",
  Briefing: "w-48",
  Renewal: "w-36",
};

export const formatMoney = (n?: number | null) =>
  n == null
    ? "—"
    : new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(n);

const titleCase = (v: string) =>
  v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

const inReview = (s?: AccountPlanStatus) =>
  s?.review_status === "draft" || s?.review_status === "in_review";

const Badge: React.FC<{ cls: string; children: React.ReactNode }> = ({
  cls,
  children,
}) => (
  <span
    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-0.5 text-[11px] font-semibold ${cls}`}
  >
    {children}
  </span>
);
const None = () => <Badge cls="bg-gray-100 text-gray-500">—</Badge>;

export const riskCls: Record<string, string> = {
  high: "bg-rose-100 text-rose-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-emerald-100 text-emerald-700",
};

/** The renewal month, with the renewal-risk badge when there is one. */
export const RenewalCell: React.FC<{
  date: string | null;
  risk?: string | null;
  parse: (v: string) => Date;
  /** The risk chip under the date, for a narrow column (F-UF-E12). */
  stacked?: boolean;
}> = ({ date, risk, parse, stacked = false }) => {
  const level = (risk || "").toLowerCase();
  return (
    <div
      className={
        stacked
          ? "flex flex-col items-start gap-1"
          : "flex items-center gap-1.5 whitespace-nowrap"
      }
    >
      <span className="whitespace-nowrap text-gray-700">
        {date
          ? parse(date).toLocaleDateString(undefined, {
              month: "short",
              year: "numeric",
            })
          : "—"}
      </span>
      {level && (
        <Badge cls={riskCls[level] || "bg-gray-100 text-gray-600"}>
          {titleCase(risk || "")}
        </Badge>
      )}
    </div>
  );
};

interface TileSpec {
  label: string;
  value: React.ReactNode;
  meta: string;
  tone?: string;
}

const Tile: React.FC<TileSpec> = ({
  label,
  value,
  meta,
  tone = "text-gray-900",
}) => (
  <div
    className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm"
    data-kpi={label}
  >
    <div className="font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">
      {label}
    </div>
    <div className={`my-1 text-3xl font-bold leading-tight ${tone}`}>
      {value}
    </div>
    <div className="text-xs text-gray-500">{meta}</div>
  </div>
);

/** The sponsor's Briefing column. */
const BriefingCell: React.FC<{ s?: AccountPlanStatus }> = ({ s }) => {
  // The sponsor reads the newest usable version: a "Draft" pill until it is
  // published, then "Published <date>" (Q1 = C + A, 2026-10-19).
  if (s?.viewer_ready && s.view_state)
    return (
      <PlanStateLabel state={s.view_state} publishedAt={s.view_published_at} />
    );
  if (s?.review_status === "published" && s.published)
    return (
      <Badge cls="bg-emerald-100 text-emerald-800">
        <BadgeCheck className="h-3 w-3" aria-hidden />
        Published · {shortDate(s.published.at)}
      </Badge>
    );
  if (inReview(s))
    return (
      <Badge cls="bg-amber-100 text-amber-800">
        <Hourglass className="h-3 w-3" aria-hidden />
        Draft · under review {daysSince(s?.review_started_at) ?? 0}d
      </Badge>
    );
  if (s?.latest_file)
    return <Badge cls="bg-gray-100 text-gray-600">Briefing pending</Badge>;
  return <Badge cls="bg-gray-100 text-gray-500">No plan yet</Badge>;
};

const SignalsCell: React.FC<{ s?: AccountPlanStatus }> = ({ s }) => {
  if (!s?.latest_file)
    return <Badge cls="bg-gray-100 text-gray-600">No plan ingested</Badge>;
  if (s.high_risks > 0)
    return (
      <Badge cls="bg-rose-100 text-rose-700">
        {plural(s.high_risks, "top-risk")}
      </Badge>
    );
  return <None />;
};

/** The Filter popover's width (w-64), used to decide which way it opens. */
const FILTER_POP_WIDTH = 256;

/**
 * Whether a right-aligned popover under `el` would be cut off on the left by a
 * clipping ancestor (or the viewport). The header wraps at common widths and puts
 * Filter at the left of the panel, where a right-aligned menu was unreachable (F-47).
 */
const clipsLeft = (el: HTMLElement, width: number): boolean => {
  let bound = 0;
  for (let p = el.parentElement; p; p = p.parentElement) {
    if (getComputedStyle(p).overflowX !== "visible")
      bound = Math.max(bound, p.getBoundingClientRect().left);
  }
  return el.getBoundingClientRect().right - width < bound;
};

/** Closes a popover on outside click / Escape. */
const usePopover = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return { open, setOpen, ref };
};

interface Props {
  accounts: PortfolioAccountRow[];
  /** Already the version each sponsor reads (forSponsor). */
  statuses: Record<string, AccountPlanStatus>;
  portfolioLoading: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  /** The admin's "Viewing as" switch, when there is one. */
  personaSwitch: React.ReactNode;
  openBriefing: (id: string) => void;
  openQuickLook: (id: string) => void;
  parseCalendarDate: (v: string) => Date;
}

const SponsorPlansList: React.FC<Props> = ({
  accounts,
  statuses,
  portfolioLoading,
  loading,
  error,
  refresh,
  personaSwitch,
  openBriefing,
  openQuickLook,
  parseCalendarDate,
}) => {
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [query, setQuery] = useState("");
  const filterPop = usePopover();
  const [filterOpensRight, setFilterOpensRight] = useState(false);
  const filterBtn = useRef<HTMLButtonElement>(null);
  // Measured after every render while open: the header can wrap (moving Filter
  // to the panel's left edge) once statuses load, i.e. after the click.
  useLayoutEffect(() => {
    if (!filterPop.open || !filterBtn.current) return;
    const opensRight = clipsLeft(filterBtn.current, FILTER_POP_WIDTH);
    if (opensRight !== filterOpensRight) setFilterOpensRight(opensRight);
  });

  const byName = useMemo(
    () =>
      accounts
        .slice()
        .sort((a, b) => a.customer_name.localeCompare(b.customer_name)),
    [accounts]
  );
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return byName.filter(
      a =>
        (!q || a.customer_name.toLowerCase().includes(q)) &&
        matchesFilter(filter, statuses[a.customer_id])
    );
  }, [byName, query, filter, statuses]);
  const pending =
    portfolioLoading || (loading && !Object.keys(statuses).length);
  const st = (id: string): AccountPlanStatus | undefined => statuses[id];

  const tiles = useMemo((): TileSpec[] => {
    const list = accounts.map(a => statuses[a.customer_id]);
    const withBriefing = list.filter(s => hasBriefing(s));
    const published = withBriefing.filter(
      s => s?.review_status === "published"
    ).length;
    const risks = list.reduce((n, s) => n + (s?.high_risks || 0), 0);
    return [
      { label: "Accounts", value: accounts.length, meta: "in your portfolio" },
      {
        label: "Briefings available",
        value: withBriefing.length,
        meta: `${published} published · ${withBriefing.length - published} under review`,
        tone: withBriefing.length ? "text-[#8538d4]" : undefined,
      },
      {
        label: "Top-risk signals",
        value: risks,
        meta: "across briefings",
        tone: risks > 0 ? "text-rose-600" : undefined,
      },
    ];
  }, [accounts, statuses]);

  const focus = useMemo(
    () =>
      byName
        .filter(a => hasBriefing(statuses[a.customer_id]))
        .sort(
          (a, b) =>
            (statuses[b.customer_id]?.high_risks || 0) -
            (statuses[a.customer_id]?.high_risks || 0)
        )
        .slice(0, 3),
    [byName, statuses]
  );

  const filtered = filter !== "all" || query.trim() !== "";
  const spinner = (
    <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-300" aria-hidden />
  );
  const cell = (s: AccountPlanStatus | undefined, node: React.ReactNode) =>
    s || !loading ? node : spinner;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] font-semibold uppercase tracking-wider text-[#974ae4]">
            Sponsor portfolio · attention signals
          </div>
          <p className="mt-0.5 text-sm text-gray-500">
            Where to focus right now — accounts surfaced by attention signals,
            not by alphabetical order.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {personaSwitch}
          <div className="relative" ref={filterPop.ref}>
            <button
              type="button"
              ref={filterBtn}
              onClick={() => filterPop.setOpen(o => !o)}
              aria-expanded={filterPop.open}
              className={`inline-flex items-center gap-1.5 rounded-lg border bg-white px-3.5 py-2 text-[13px] font-semibold ${filtered ? "border-purple-300 text-[#8538d4]" : "border-gray-200 text-gray-600"} hover:border-purple-300 hover:bg-purple-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50`}
            >
              <Filter className="h-4 w-4" aria-hidden /> Filter
              {filtered && (
                <span className="ml-0.5 rounded-full bg-[#974ae4] px-1.5 text-[10px] font-bold text-white">
                  on
                </span>
              )}
            </button>
            {filterPop.open && (
              <div
                className={`absolute ${filterOpensRight ? "left-0" : "right-0"} z-20 mt-2 w-64 rounded-xl border border-gray-200 bg-white p-3 shadow-lg`}
              >
                <div className="relative mb-2">
                  <Search
                    className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
                    aria-hidden
                  />
                  <input
                    type="search"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Account name…"
                    aria-label="Search accounts"
                    className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-2 text-sm outline-none focus:border-purple-400"
                  />
                </div>
                <div role="radiogroup" aria-label="Plan status">
                  {FILTERS.map(f => (
                    <label
                      key={f.id}
                      className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <input
                        type="radio"
                        name="plan-status-filter"
                        checked={filter === f.id}
                        onChange={() => setFilter(f.id)}
                      />
                      {f.label}
                    </label>
                  ))}
                </div>
                {filtered && (
                  <button
                    type="button"
                    onClick={() => {
                      setFilter("all");
                      setQuery("");
                    }}
                    className="mt-2 text-xs font-semibold text-[#8538d4] hover:underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {tiles.map(t => (
          <Tile
            key={t.label}
            label={t.label}
            value={pending ? "…" : t.value}
            meta={pending ? "" : t.meta}
            tone={t.tone}
          />
        ))}
      </div>

      <BriefingQuestionsStrip />

      {!portfolioLoading && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          {focus.length === 0 ? (
            <div className="flex flex-wrap items-center gap-4 px-5 py-3.5">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Where to focus
              </span>
              <span className="flex-1 text-sm text-gray-600">
                No briefings yet. Your account team is preparing them from their
                latest plans.
              </span>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {focus.map((a, i) => {
                const risks = st(a.customer_id)?.high_risks || 0;
                return (
                  <li
                    key={a.customer_id}
                    className="flex flex-wrap items-center gap-4 px-5 py-3.5"
                  >
                    <span className="w-28 shrink-0 font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                      {i === 0 ? "Where to focus" : ""}
                    </span>
                    <Badge
                      cls={
                        risks > 0
                          ? "bg-rose-100 text-rose-700"
                          : "bg-purple-100 text-[#8538d4]"
                      }
                    >
                      FOCUS
                    </Badge>
                    <span className="min-w-0 flex-1 text-sm text-gray-800">
                      <span className="font-semibold">{a.customer_name}</span>{" "}
                      <span className="text-gray-500">
                        ·{" "}
                        {risks > 0
                          ? `${plural(risks, "top risk")} in the briefing`
                          : "Briefing ready to read"}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => openBriefing(a.customer_id)}
                      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 ${i === 0 ? "bg-[#974ae4] text-white hover:bg-[#8538d4]" : "border border-gray-200 bg-white text-gray-700 hover:border-purple-300 hover:bg-purple-50"}`}
                    >
                      <ArrowRight className="h-4 w-4" aria-hidden /> Open
                      briefing
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {error && (
        <p
          role="alert"
          className="flex items-center gap-2 text-sm text-red-600"
        >
          {error}
          <button
            type="button"
            onClick={refresh}
            className="font-medium text-red-700 underline underline-offset-2"
          >
            Try again
          </button>
        </p>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="w-full min-w-[760px] table-fixed">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
            <tr>
              {COLUMNS.map(c => (
                <th
                  key={c}
                  className={`px-3 py-3 first:pl-5 ${COLUMN_WIDTH[c] ?? ""}`}
                >
                  {c}
                </th>
              ))}
              <th className="w-[4.5rem] px-3 py-3" aria-label="Actions" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length + 1}
                  className="px-4 py-8 text-center text-sm text-gray-500"
                >
                  {portfolioLoading ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2
                        className="h-4 w-4 animate-spin text-[#974ae4]"
                        aria-hidden
                      />
                      Loading your accounts…
                    </span>
                  ) : accounts.length ? (
                    "No accounts match the filter."
                  ) : (
                    "No accounts in your portfolio yet."
                  )}
                </td>
              </tr>
            ) : (
              rows.map(a => {
                const id = a.customer_id;
                const s = st(id);
                return (
                  <tr
                    key={id}
                    onClick={() => openBriefing(id)}
                    onKeyDown={e =>
                      e.key === "Enter" &&
                      e.target === e.currentTarget &&
                      openBriefing(id)
                    }
                    tabIndex={0}
                    className="cursor-pointer transition hover:bg-purple-50/40 focus:bg-purple-50/40 focus:outline-none"
                  >
                    <td className="py-3.5 pl-5 pr-3">
                      <div
                        className="line-clamp-2 break-words font-semibold leading-snug text-gray-900"
                        title={a.customer_name}
                      >
                        {a.customer_name}
                      </div>
                      {a.industry && (
                        <div className="mt-0.5 truncate text-xs text-gray-500">
                          {a.industry}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3.5">
                      {cell(s, <SignalsCell s={s} />)}
                    </td>
                    <td className="px-3 py-3.5 text-sm font-medium text-gray-700">
                      {formatMoney(a.revenue)}
                    </td>
                    <td className="px-3 py-3.5 text-sm">
                      <RenewalCell
                        date={a.renewal_date}
                        risk={s?.renewal_risk}
                        parse={parseCalendarDate}
                      />
                    </td>
                    <td className="px-3 py-3.5">
                      {cell(s, <BriefingCell s={s} />)}
                    </td>
                    <td className="whitespace-nowrap py-3.5 pl-1 pr-3 text-right">
                      <button
                        type="button"
                        title="Quick look"
                        aria-label={`Quick look at ${a.customer_name}`}
                        onClick={e => {
                          e.stopPropagation();
                          openQuickLook(id);
                        }}
                        className="inline-flex items-center rounded-md p-1 text-gray-500 hover:bg-purple-50 hover:text-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                      >
                        <Eye className="h-4 w-4" aria-hidden />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <p className="flex items-center gap-2 text-xs text-gray-400">
        <Info className="h-3.5 w-3.5" aria-hidden />
        Click a row to open its briefing · the eye icon gives a quick look
        without leaving this page.
      </p>
    </div>
  );
};

export default SponsorPlansList;
